import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged, signOut, deleteUser, updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import '../css/components/change-setting-modal.css';
import InputModal from './InputModal';

const SettingsPage = () => {
  // User and auth state
  const [user, setUser] = useState(null);
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSection, setActiveSection] = useState('Profile');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [pendingDelete, setPendingDelete] = useState(false);
  // New modal states for name/username
  const [showNameModal, setShowNameModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  // Initialize mobile menu visibility on page load
  useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth <= 1024) {
        setShowMobileMenu(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Authentication state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        loadUserData(currentUser);
      } else {
        window.location.href = "/";
      }
    });

    return () => unsubscribe();
  }, []);

  // Load user data from Firestore
  const loadUserData = async (currentUser) => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setFullName(userData.fullName || currentUser.displayName || '');
        setUsername(userData.username || currentUser.email?.split('@')[0] || '');
      } else {
        // Create initial user document if it doesn't exist
        const initialUserData = {
          fullName: currentUser.displayName || '',
          username: currentUser.email?.split('@')[0] || '',
          email: currentUser.email,
          createdAt: new Date()
        };
        await setDoc(doc(db, 'users', currentUser.uid), initialUserData);
        setFullName(initialUserData.fullName);
        setUsername(initialUserData.username);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      
      // Set fallback values even if Firestore fails
      setFullName(currentUser.displayName || '');
      setUsername(currentUser.email?.split('@')[0] || '');
      
      setError(`Failed to load user data: ${error.message}`);
    }
  };

  // Handle full name change
  const handleChangeFullName = () => {
    setShowNameModal(true);
  };

  const handleNameModalSubmit = async (newFullName) => {
    if (!newFullName || newFullName === fullName) {
      setShowNameModal(false);
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      await setDoc(doc(db, 'users', user.uid), { fullName: newFullName }, { merge: true });
      await updateProfile(user, { displayName: newFullName });
      setFullName(newFullName);
      setSuccess('Full name updated successfully');
    } catch (error) {
      console.error('Error updating full name:', error);
      setError('Failed to update full name');
    } finally {
      setIsLoading(false);
      setShowNameModal(false);
    }
  };

  // Handle username change
  const handleChangeUsername = () => {
    setShowUsernameModal(true);
  };

  const handleUsernameModalSubmit = async (newUsername) => {
    if (!newUsername || newUsername === username) {
      setShowUsernameModal(false);
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      await setDoc(doc(db, 'users', user.uid), { username: newUsername }, { merge: true });
      setUsername(newUsername);
      setSuccess('Username updated successfully');
    } catch (error) {
      console.error('Error updating username:', error);
      setError('Failed to update username');
    } finally {
      setIsLoading(false);
      setShowUsernameModal(false);
    }
  };

  // Sign out functionality
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.href = "/";
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out');
    }
  };

  // Delete account functionality
  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmation = () => {
    setShowDeleteModal(false);
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setShowDeleteConfirmModal(false);
    setShowReauthModal(false);
    setReauthPassword('');
    setPendingDelete(false);
  };

  const handleFinalDeleteConfirmation = async () => {
    setIsLoading(true);
    setError('');
    try {
      // Delete user document from Firestore
      await deleteDoc(doc(db, 'users', user.uid));
      // Delete user account from Firebase Auth
      await deleteUser(user);
      window.location.href = "/";
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/requires-recent-login') {
        setShowReauthModal(true);
        setPendingDelete(true);
        setShowDeleteConfirmModal(false);
        setError('Please re-authenticate to delete your account.');
      } else {
        setError('Failed to delete account. You may need to re-authenticate first.');
      }
    } finally {
      setIsLoading(false);
      if (!pendingDelete) setShowDeleteConfirmModal(false);
    }
  };

  // Handle menu item click
  const handleMenuItemClick = (section) => {
    setActiveSection(section);
    setShowMobileMenu(false);
  };

  // Utility functions
  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };

  const renderModal = (modalContent) => createPortal(modalContent, document.body);

  const getProviderId = () => user?.providerData?.[0]?.providerId || '';
  const isGoogleUser = getProviderId() === 'google.com';

  return (
    <div className="settings-page">
      {/* Mobile Menu Button - Top Left Corner */}
      <button className="mobile-menu-btn" onClick={() => setShowMobileMenu(!showMobileMenu)}>
        <i className="fa fa-bars"></i>
      </button>
      
      <div className="settings-container">
        {/* Settings Menu Sidebar */}
        <div className={`settings-menu ${showMobileMenu ? 'show' : ''}`}>
          <div className="settings-menu-section">
            <h3 className="menu-section-title">Settings</h3>
            <div 
              className={`menu-item ${activeSection === 'Profile' ? 'active' : ''}`}
              onClick={() => {
                setActiveSection('Profile');
                setShowMobileMenu(false);
              }}
            >
              <i className="fa fa-user"></i>
              Profile
            </div>
            {/* Billing section removed */}
            <div 
              className={`menu-item ${activeSection === 'System' ? 'active' : ''}`}
              onClick={() => {
                setActiveSection('System');
                setShowMobileMenu(false);
              }}
            >
              <i className="fa fa-cog"></i>
              System
            </div>
          </div>
          {/* Back button at the bottom of sidebar */}
          <div className="settings-menu-footer">
            <button 
              className="settings-back-btn"
              onClick={() => window.location.href = "/platform"}
            >
              <i className="fa fa-arrow-left"></i>
              <span className="back-text">Back</span>
            </button>
          </div>
        </div>

        {/* Settings Content */}
        <div className="settings-content-area">
          {/* Settings Header */}
          <div className="settings-header"></div>
          <div className="settings-content">
            {/* Error and Success Messages */}
            {error && (
              <div style={{ 
                background: '#fee', 
                color: '#c33', 
                padding: '12px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '1px solid #fcc'
              }}>
                {error}
              </div>
            )}
            {success && (
              <div style={{ 
                background: '#efe', 
                color: '#363', 
                padding: '12px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '1px solid #cfc'
              }}>
                {success}
              </div>
            )}

            {/* Conditional Content Based on Active Section */}
            {activeSection === 'Profile' && (
              <div className="settings-section">
                <h2 className="settings-section-title">{activeSection}</h2>
                {/* User Profile */}
                <div className="user-profile">
                  <div className="user-avatar">
                    {getInitials(fullName || user?.displayName || user?.email)}
                  </div>
                  <div className="user-info">
                    <div className="user-name">{fullName || user?.displayName || 'User'}</div>
                    <div className="user-username">{username || user?.email?.split('@')[0] || 'username'}</div>
                  </div>
                </div>
                {/* User Details */}
                <div className="user-detail user-detail-with-button">
                  <div className="user-detail-content">
                    <div className="detail-label">Full Name</div>
                    <div className="detail-value">{fullName || user?.displayName || 'Not set'}</div>
                  </div>
                  <button 
                    className="change-btn" 
                    onClick={handleChangeFullName}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Updating...' : 'Change name'}
                  </button>
                </div>
                <div className="user-detail user-detail-with-button">
                  <div className="user-detail-content">
                    <div className="detail-label">Username</div>
                    <div className="detail-value">{username || user?.email?.split('@')[0] || 'Not set'}</div>
                  </div>
                  <button 
                    className="change-btn" 
                    onClick={handleChangeUsername}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Updating...' : 'Change username'}
                  </button>
                </div>
                <div className="user-detail">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{user?.email || 'Not available'}</div>
                </div>
                {/* Subscription Section (copied from Billing) */}
                <div className="subscription-content">
                  <div className="subscription-text">
                    <div className="subscription-title">Analyze smarter. Model faster. Discover more—with Priori Pro.</div>
                    <div className="subscription-subtitle">Get the most out of Priori with Pro.</div>
                  </div>
                  <button className="upgrade-btn">Upgrade plan</button>
                </div>
              </div>
            )}

            {/* Billing section removed */}

            {activeSection === 'System' && (
              <div className="settings-section">
                <h2 className="settings-section-title">{activeSection}</h2>
                <div className="system-item">
                  <div className="system-label">Support</div>
                  <button
                    className="system-btn"
                    onClick={() => window.location.href = 'mailto:info.prioriai@gmail.com'}
                  >
                    Contact
                  </button>
                </div>
                <div className="system-item">
                  <div className="system-content">
                    <div className="system-label">You are signed in as {username || user?.email?.split('@')[0] || 'user'}</div>
                  </div>
                  <button 
                    className="system-btn" 
                    onClick={handleSignOut}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Signing out...' : 'Sign out'}
                  </button>
                </div>
                <div className="system-item">
                  <div className="system-content">
                    <div className="system-label">Delete account</div>
                    <div className="system-subtitle">Permanently delete your account and data</div>
                  </div>
                  <button 
                    className="system-btn learn-more-btn" 
                    onClick={handleDeleteAccount}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Deleting...' : 'Delete account'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* First Delete Confirmation Modal */}
      {showDeleteModal && renderModal(
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-body">
              <div className="modal-title-group">
                <div className="modal-title">Delete account</div>
                <div className="modal-desc">  Deleting your account is a permanent action. All your account and conversation data will be permanently lost and cannot be recovered.</div>
              </div>
              <div className="modal-actions">
                <button className="delete-understand-btn" onClick={handleDeleteConfirmation}>I Understand</button>
                <button className="delete-cancel-btn" onClick={handleDeleteCancel}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Second Delete Confirmation Modal */}
      {showDeleteConfirmModal && renderModal(
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-body">
              <div className="modal-title-group">
                <div className="modal-title">Final confirmation</div>
                <div className="modal-desc">Are you sure you want to delete your account? This action is permanent and cannot be undone.</div>
              </div>
              <div className="modal-actions">
                <button 
                  className="delete-final-btn" 
                  onClick={handleFinalDeleteConfirmation}
                  disabled={isLoading}
                >
                  {isLoading ? 'Deleting...' : 'Delete Account'}
                </button>
                <button className="delete-cancel-btn" onClick={handleDeleteCancel}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Re-authentication Modal */}
      {showReauthModal && renderModal(
        isGoogleUser ? (
          <div className="modal-overlay" onClick={handleDeleteCancel}>
            <div className="delete-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-body">
                <div className="modal-title-group">
                  <div className="modal-title">Re-authenticate with Google</div>
                  <div className="modal-desc">For security, please re-authenticate with Google to confirm account deletion.</div>
                </div>
                <div className="modal-actions">
                  <button
                    className="delete-final-btn"
                    onClick={async () => {
                      setIsLoading(true);
                      setError('');
                      try {
                        const { GoogleAuthProvider, reauthenticateWithPopup } = await import('firebase/auth');
                        const provider = new GoogleAuthProvider();
                        await reauthenticateWithPopup(user, provider);
                        setShowReauthModal(false);
                        setPendingDelete(false);
                        // Retry deletion
                        await deleteDoc(doc(db, 'users', user.uid));
                        await deleteUser(user);
                        window.location.href = "/";
                      } catch (err) {
                        console.error('Google re-authentication or deletion failed:', err);
                        setError('Google re-authentication failed. Please try again.');
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Deleting...' : 'Re-authenticate & Delete'}
                  </button>
                  <button className="delete-cancel-btn" onClick={handleDeleteCancel} disabled={isLoading}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="modal-overlay" onClick={handleDeleteCancel}>
            <div className="delete-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-body">
                <div className="modal-title-group">
                  <div className="modal-title">Re-authenticate</div>
                  <div className="modal-desc">For security, please enter your password to confirm account deletion.</div>
                </div>
                <input
                  type="password"
                  className="delete-confirm-input"
                  placeholder="Enter your password"
                  value={reauthPassword}
                  onChange={e => setReauthPassword(e.target.value)}
                  disabled={isLoading}
                />
                <div className="modal-actions">
                  <button
                    className="delete-final-btn"
                    onClick={async () => {
                      setIsLoading(true);
                      setError('');
                      try {
                        const { EmailAuthProvider, reauthenticateWithCredential } = await import('firebase/auth');
                        const credential = EmailAuthProvider.credential(user.email, reauthPassword);
                        await reauthenticateWithCredential(user, credential);
                        setShowReauthModal(false);
                        setReauthPassword('');
                        setPendingDelete(false);
                        // Retry deletion
                        await deleteDoc(doc(db, 'users', user.uid));
                        await deleteUser(user);
                        window.location.href = "/";
                      } catch (err) {
                        console.error('Re-authentication or deletion failed:', err);
                        setError('Re-authentication failed. Please check your password and try again.');
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading || !reauthPassword}
                  >
                    {isLoading ? 'Deleting...' : 'Confirm & Delete'}
                  </button>
                  <button className="delete-cancel-btn" onClick={handleDeleteCancel} disabled={isLoading}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* InputModal for changing full name */}
      {showNameModal && (
        <InputModal
          open={showNameModal}
          title="Change Full Name"
          initialValue={fullName}
          onCancel={() => setShowNameModal(false)}
          onSubmit={handleNameModalSubmit}
          placeholder="Enter your new full name"
        />
      )}
      {/* InputModal for changing username */}
      {showUsernameModal && (
        <InputModal
          open={showUsernameModal}
          title="Change Username"
          initialValue={username}
          onCancel={() => setShowUsernameModal(false)}
          onSubmit={handleUsernameModalSubmit}
          placeholder="Enter your new username"
        />
      )}
    </div>
  );
};

export default SettingsPage;
